/**
 * Status da licitação (12): nomes, chip e o seletor único de mudança de status (D86), usado na lista e na ficha.
 * O banco valida as mesmas transições (privado.transicao_licitacao_valida) e grava a linha do tempo.
 */
import { useState, type MouseEvent } from "react";
import { useGetOne, useNotify, useRefresh } from "react-admin";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import SetaIcon from "@mui/icons-material/ArrowDropDown";
import AlertaIcon from "@mui/icons-material/WarningAmberOutlined";
import BandeiraIcon from "@mui/icons-material/SportsScoreOutlined";
import { supabase } from "../lib/supabase";

export const STATUS_LICITACAO = [
  { id: "captada", name: "Captada" },
  { id: "em_analise", name: "Em análise" },
  { id: "em_cotacao", name: "Em cotação" },
  { id: "proposta_pronta", name: "Proposta pronta" },
  { id: "proposta_enviada", name: "Proposta enviada" },
  { id: "em_disputa", name: "Em disputa" },
  { id: "resultado", name: "Resultado" },
  { id: "recurso", name: "Recurso" },
  { id: "homologada", name: "Homologada" },
  { id: "suspensa", name: "Suspensa" },
  { id: "descartada", name: "Descartada" },
];

export const nomeStatus = (s: string | null | undefined) => STATUS_LICITACAO.find((x) => x.id === s)?.name ?? s ?? "—";

export interface Acao {
  para: string;
  rotulo: string;
}

/**
 * Transições manuais permitidas (as mesmas de privado.transicao_licitacao_valida). As automáticas (D95) são:
 * captada → em análise ao começar a precificar; → em cotação no primeiro item com preço; → proposta pronta ao
 * finalizar a precificação (D101); proposta enviada → em disputa no horário da sessão.
 */
const TRANSICOES: Record<string, string[]> = {
  captada: ["em_analise", "em_cotacao", "suspensa", "descartada"],
  em_analise: ["em_cotacao", "suspensa", "descartada"],
  em_cotacao: ["proposta_pronta", "proposta_enviada", "em_analise", "suspensa", "descartada"],
  proposta_pronta: ["proposta_enviada", "em_cotacao", "suspensa", "descartada"],
  proposta_enviada: ["em_disputa", "proposta_pronta", "em_cotacao", "suspensa"],
  em_disputa: ["resultado", "proposta_enviada", "suspensa"],
  resultado: ["homologada", "recurso", "em_disputa", "suspensa"],
  recurso: ["homologada", "resultado", "suspensa"],
  suspensa: ["retomar", "descartada"],
  descartada: ["em_analise"],
};

/** Por que um status não pode ser escolhido a partir do atual (mostrado no menu). */
const MOTIVO_BLOQUEIO: Record<string, string> = {
  captada: "Só no cadastro",
  em_analise: "Automático ao começar a precificar",
  em_cotacao: "Automático quando um item recebe preço",
  proposta_pronta: "Use “Finalizar precificação” na aba Precificação",
  proposta_enviada: "Depois da proposta pronta, ao lançar no portal",
  em_disputa: "Automático no horário da sessão",
  resultado: "Só depois da disputa (botão “Disputa encerrada”)",
  recurso: "Só depois do resultado",
  homologada: "Só depois do resultado",
  suspensa: "Não se aplica a esta etapa",
  descartada: "Depois da proposta enviada, registre o resultado",
};

/** Descartar e suspender pedem motivo; as demais mudanças são imediatas. */
const PEDE_MOTIVO = new Set(["descartada", "suspensa"]);

/** Cor de cada status (fundo, texto e borda), a mesma no chip e no menu. */
const COR_STATUS: Record<string, { fundo: string; texto: string; borda: string }> = {
  captada: { fundo: "transparent", texto: "#33474f", borda: "#8a979d" },
  em_analise: { fundo: "#e3e7e9", texto: "#0d2733", borda: "#e3e7e9" },
  em_cotacao: { fundo: "#33474f", texto: "#ffffff", borda: "#33474f" },
  proposta_pronta: { fundo: "transparent", texto: "#012639", borda: "#012639" },
  proposta_enviada: { fundo: "#012639", texto: "#ffffff", borda: "#012639" },
  em_disputa: { fundo: "#00B0B3", texto: "#ffffff", borda: "#00B0B3" },
  resultado: { fundo: "transparent", texto: "#00787a", borda: "#00B0B3" },
  recurso: { fundo: "transparent", texto: "#a35a00", borda: "#ed6c02" },
  homologada: { fundo: "#2e7d32", texto: "#ffffff", borda: "#2e7d32" },
  suspensa: { fundo: "#ed6c02", texto: "#ffffff", borda: "#ed6c02" },
  descartada: { fundo: "transparent", texto: "#c62828", borda: "#e57373" },
};

/** Pode mudar de `de` para `para` à mão? (Mesmas regras do menu e do banco.) */
export const transicaoPermitida = (de: string, para: string) => (TRANSICOES[de] ?? []).includes(para);
/** Por que não pode (texto do menu). */
export const motivoBloqueio = (para: string) => MOTIVO_BLOQUEIO[para] ?? "Mudança não permitida";
export const pedeMotivo = (para: string) => PEDE_MOTIVO.has(para);
/** Cor do status (borda) para cabeçalhos e marcações. */
export const corStatus = (status: string) => COR_STATUS[status]?.borda ?? "#8a979d";

export const estiloStatus = (status: string) => {
  const c = COR_STATUS[status] ?? { fundo: "transparent", texto: "#33474f", borda: "#8a979d" };
  return {
    bgcolor: c.fundo,
    color: c.texto,
    border: "1px solid",
    borderColor: c.borda,
    fontWeight: 600,
    "& .MuiChip-deleteIcon": { color: c.texto, opacity: 0.8, "&:hover": { color: c.texto, opacity: 1 } },
    "&:hover, &:focus": { bgcolor: c.fundo, filter: "brightness(0.95)" },
  };
};

export function ChipStatus({ status }: { status: string }) {
  return <Chip size="small" label={nomeStatus(status)} sx={estiloStatus(status)} />;
}

export async function mudarStatus(licitacaoId: string, para: string, observacao: string | null) {
  const { error } = await supabase.rpc("mudar_status_licitacao", { licitacao_id: licitacaoId, novo_status: para, observacao });
  if (error) throw new Error(error.message);
}

export function DialogoMotivo({ licitacaoId, acao, aoFechar }: { licitacaoId: string; acao: Acao; aoFechar: () => void }) {
  const notify = useNotify();
  const refresh = useRefresh();
  const [motivo, setMotivo] = useState("");
  const [gravando, setGravando] = useState(false);
  const descarte = acao.para === "descartada";

  async function confirmar() {
    setGravando(true);
    try {
      await mudarStatus(licitacaoId, acao.para, motivo.trim());
      notify(`Status: ${nomeStatus(acao.para)}.`, { type: "success" });
      refresh();
      aoFechar();
    } catch (e) {
      notify((e as Error).message, { type: "error" });
    } finally {
      setGravando(false);
    }
  }

  return (
    <Dialog open onClose={gravando ? undefined : aoFechar} fullWidth maxWidth="sm" onClick={(e) => e.stopPropagation()}>
      <DialogTitle>{descarte ? "Descartar licitação" : "Suspender licitação"}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          label={descarte ? "Motivo do descarte" : "Motivo da suspensão"}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          required
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={aoFechar} disabled={gravando}>
          Cancelar
        </Button>
        <Button variant="contained" color={descarte ? "error" : "primary"} onClick={() => void confirmar()} disabled={gravando || !motivo.trim()}>
          {gravando ? "Gravando…" : "Confirmar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Seletor de status: o chip do status atual abre o menu com as mudanças permitidas.
 * Funciona dentro de linhas clicáveis (não propaga o clique).
 */
export function SeletorStatus({ licitacaoId, status, grande = false }: { licitacaoId: string; status: string; grande?: boolean }) {
  const notify = useNotify();
  const refresh = useRefresh();
  const [ancora, setAncora] = useState<HTMLElement | null>(null);
  const [comMotivo, setComMotivo] = useState<Acao | null>(null);
  const [gravando, setGravando] = useState(false);
  const permitidos = new Set(TRANSICOES[status] ?? []);

  function abrir(e: MouseEvent<HTMLElement>) {
    // A linha da lista é um link: o clique no chip não pode abrir a ficha.
    e.stopPropagation();
    e.preventDefault();
    setAncora(e.currentTarget);
  }

  async function escolher(a: Acao) {
    setAncora(null);
    if (PEDE_MOTIVO.has(a.para)) {
      setComMotivo(a);
      return;
    }
    setGravando(true);
    try {
      await mudarStatus(licitacaoId, a.para, null);
      notify(a.para === "retomar" ? "Licitação retomada na etapa anterior à suspensão." : `Status: ${nomeStatus(a.para)}.`, { type: "success" });
      refresh();
    } catch (e) {
      notify((e as Error).message, { type: "error" });
    } finally {
      setGravando(false);
    }
  }

  const opcoes = [
    ...(status === "suspensa" ? [{ id: "retomar", name: "Retomar (volta à etapa anterior)" }] : []),
    ...STATUS_LICITACAO,
  ];

  return (
    <>
      <Chip
        size={grande ? "medium" : "small"}
        label={gravando ? "Gravando…" : nomeStatus(status)}
        onClick={abrir}
        onDelete={abrir}
        deleteIcon={<SetaIcon />}
        disabled={gravando}
        aria-label={`Status ${nomeStatus(status)}. Mudar status`}
        sx={{ ...estiloStatus(status), ...(grande ? { fontWeight: 700 } : {}) }}
      />
      <Menu
        anchorEl={ancora}
        open={Boolean(ancora)}
        onClose={() => setAncora(null)}
        onClick={(e) => e.stopPropagation()}
        slotProps={{ paper: { sx: { minWidth: 300 } } }}
      >
        {opcoes.map((o) => {
          const atual = o.id === status;
          const permitido = permitidos.has(o.id);
          return (
            <MenuItem
              key={o.id}
              disabled={!permitido}
              selected={atual}
              onClick={() => void escolher({ para: o.id, rotulo: o.name })}
              sx={{ py: 0.75, "&.Mui-disabled": { opacity: 1 } }}
            >
              <Stack direction="row" spacing={1.25} alignItems="center" sx={{ width: "100%" }}>
                <Chip size="small" label={o.id === "retomar" ? "Retomar" : o.name} sx={{ ...estiloStatus(o.id === "retomar" ? "em_analise" : o.id), opacity: permitido || atual ? 1 : 0.45, minWidth: 128 }} />
                <Typography variant="caption" color={permitido ? "text.primary" : "text.disabled"} sx={{ whiteSpace: "normal", lineHeight: 1.3 }}>
                  {atual ? "Status atual" : permitido ? (PEDE_MOTIVO.has(o.id) ? "Pede o motivo" : o.id === "retomar" ? o.name : "Mudar agora") : MOTIVO_BLOQUEIO[o.id]}
                </Typography>
              </Stack>
            </MenuItem>
          );
        })}
      </Menu>
      {comMotivo && <DialogoMotivo licitacaoId={licitacaoId} acao={comMotivo} aoFechar={() => setComMotivo(null)} />}
    </>
  );
}

/**
 * Ação da etapa na ficha (D95, D101): proposta pronta, "Proposta enviada no portal"; em disputa, "Disputa encerrada" leva a Resultado; em resultado, cobra o registro
 * quando falta resultado em algum item disputado.
 */
export function AcaoDaEtapa({ licitacaoId, status, grande = false }: { licitacaoId: string; status: string; grande?: boolean }) {
  const notify = useNotify();
  const refresh = useRefresh();
  const [gravando, setGravando] = useState(false);
  const { data: resumo } = useGetOne<{ id: string; resultado_geral: string | null }>(
    "v_licitacao",
    { id: licitacaoId },
    { enabled: status === "resultado" || status === "recurso" || status === "homologada" },
  );
  if (status === "proposta_pronta") {
    return (
      <Button
        size="small"
        variant="contained"
        disabled={gravando}
        onClick={() => {
          setGravando(true);
          void mudarStatus(licitacaoId, "proposta_enviada", "Proposta lançada no portal")
            .then(() => {
              notify("Status: proposta enviada.", { type: "success" });
              refresh();
            })
            .catch((e: Error) => notify(e.message, { type: "error" }))
            .finally(() => setGravando(false));
        }}
      >
        Proposta enviada no portal
      </Button>
    );
  }
  if (status === "em_disputa") {
    return (
      <Button
        size={grande ? "large" : "small"}
        variant="contained"
        color="secondary"
        disabled={gravando}
        startIcon={grande ? <BandeiraIcon /> : undefined}
        sx={grande ? { px: 3, fontSize: "1rem", fontWeight: 700, whiteSpace: "nowrap" } : undefined}
        onClick={() => {
          setGravando(true);
          void mudarStatus(licitacaoId, "resultado", "Disputa encerrada")
            .then(() => {
              notify("Disputa encerrada. Registre o resultado de cada item na aba Disputa.", { type: "success" });
              refresh();
            })
            .catch((e: Error) => notify(e.message, { type: "error" }))
            .finally(() => setGravando(false));
        }}
      >
        Disputa encerrada
      </Button>
    );
  }
  if (resumo && (resumo.resultado_geral === null || resumo.resultado_geral === "pendente")) {
    return <Chip size="small" color="warning" icon={<AlertaIcon />} label={resumo.resultado_geral === null ? "Registrar o resultado" : "Resultado incompleto"} />;
  }
  return null;
}
